/**
 * SCOPE entry gate.
 *
 * Shown on the /scope landing BEFORE any scope work can begin.
 *
 *  Step 1 (gate): if user is not logged in, a hovering card asks them to
 *                 create a free account (email + password). Background is
 *                 blurred. Existing users can sign in.
 *  Step 2 (choose): once logged in, the gate fades and reveals three
 *                   routes — upload a plan, enter dimensions, or the
 *                   coming-soon AI Vision (BETA, greyed out).
 *
 * Keeps the UI consciously simple: no CSS libraries, inline styles to
 * match the rest of the SCOPE tool.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { FileImage, Ruler, Sparkles, Check, ArrowRight, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AuthCard({ onAuthed }) {
  const { register, login } = useAuth();
  const [mode, setMode] = useState('register'); // 'register' | 'login'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [terms, setTerms] = useState(false);
  const [marketing, setMarketing] = useState(false); // §7.6 default UNticked
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!email || !password) return setErr('Email and password are required.');
    if (password.length < 8) return setErr('Password must be at least 8 characters.');
    if (mode === 'register' && !terms) return setErr('Please accept the Terms and Conditions.');
    setBusy(true);
    try {
      if (mode === 'register') {
        await register({
          email: email.trim().toLowerCase(),
          password,
          terms_accepted: true,
          marketing_consent: marketing,
        });
      } else {
        await login({ email: email.trim().toLowerCase(), password });
      }
      if (typeof onAuthed === 'function') onAuthed();
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : (detail || e2?.response?.data?.error || e2.message);
      setErr(msg || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      data-testid="scope-entry-auth-card"
      onSubmit={submit}
      style={{
        width: '100%', maxWidth: 460,
        background: '#fff', borderRadius: 18,
        border: '1.5px solid #E2E8F0',
        boxShadow: '0 24px 60px -20px rgba(15, 39, 66, 0.35)',
        padding: '28px 30px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#0F2742', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Lock className="w-4 h-4" style={{ color: '#fff' }} />
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', letterSpacing: '.08em', textTransform: 'uppercase' }}>
          Account required
        </div>
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0F172A', margin: '4px 0 6px', letterSpacing: '-0.01em' }}>
        {mode === 'register' ? 'Create your free Schedulesofwork.com account' : 'Sign in to continue'}
      </h2>
      <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.55, marginBottom: 18 }}>
        {mode === 'register'
          ? 'An account saves your drawings, rooms and quotes — so you can come back and finish later.'
          : 'Welcome back. Sign in to resume your saved projects.'}
      </p>

      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Email</label>
      <input
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        data-testid="scope-entry-email"
        style={{
          width: '100%', padding: '11px 14px', borderRadius: 10,
          border: '1.5px solid #CBD5E1', fontSize: 14, marginBottom: 12, outline: 'none',
        }}
      />

      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Password</label>
      <input
        type="password"
        autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
        required
        minLength={8}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        data-testid="scope-entry-password"
        style={{
          width: '100%', padding: '11px 14px', borderRadius: 10,
          border: '1.5px solid #CBD5E1', fontSize: 14, marginBottom: 12, outline: 'none',
        }}
      />
      {mode === 'register' && (
        <div style={{ fontSize: 11, color: '#64748B', marginBottom: 14 }}>At least 8 characters.</div>
      )}

      {mode === 'register' && (
        <>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 12, color: '#334155', marginBottom: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
              data-testid="scope-entry-terms"
              style={{ marginTop: 3 }}
            />
            <span>
              I accept the <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#2563EB' }}>Terms and Conditions</a> and Privacy Policy.
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 12, color: '#334155', marginBottom: 14, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              data-testid="scope-entry-marketing"
              style={{ marginTop: 3 }}
            />
            <span>
              Help me get the best value on my project — send me contractor rate insights, cost-saving tips and market updates relevant to my refurbishment.
            </span>
          </label>
        </>
      )}

      {err && (
        <div data-testid="scope-entry-error" style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', padding: '10px 12px', borderRadius: 8, fontSize: 12, marginBottom: 12 }}>
          {err}
        </div>
      )}

      <button
        type="submit"
        disabled={busy}
        data-testid="scope-entry-submit"
        style={{
          width: '100%', background: '#0F2742', color: '#fff', border: 'none',
          padding: '12px 0', borderRadius: 10, fontSize: 14, fontWeight: 700,
          cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.55 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        {busy ? 'Please wait…' : (mode === 'register' ? 'Create account and continue' : 'Sign in and continue')}
        {!busy && <ArrowRight className="w-4 h-4" />}
      </button>

      <div style={{ fontSize: 12, color: '#64748B', marginTop: 14, textAlign: 'center' }}>
        {mode === 'register' ? (
          <>Already have an account?{' '}
            <button type="button" onClick={() => { setMode('login'); setErr(''); }} data-testid="scope-entry-switch-login" style={{ background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', fontWeight: 600 }}>
              Sign in
            </button>
          </>
        ) : (
          <>New here?{' '}
            <button type="button" onClick={() => { setMode('register'); setErr(''); }} data-testid="scope-entry-switch-register" style={{ background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', fontWeight: 600 }}>
              Create an account
            </button>
          </>
        )}
      </div>
    </form>
  );
}

function MethodCard({ icon, badge, title, body, cta, onClick, disabled, testid, highlight }) {
  const card = (
    <div
      style={{
        background: disabled ? '#F8FAFC' : '#fff',
        borderRadius: 14,
        border: `1.5px solid ${highlight ? '#2563EB' : '#E2E8F0'}`,
        padding: '24px 22px',
        display: 'flex', flexDirection: 'column', gap: 14,
        minHeight: 260,
        position: 'relative',
        boxShadow: highlight ? '0 10px 30px -15px rgba(37, 99, 235, 0.4)' : '0 1px 3px rgba(0,0,0,0.04)',
        opacity: disabled ? 0.65 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'transform .15s ease, box-shadow .15s ease',
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.transform = 'translateY(0)'; }}
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
    >
      {badge && (
        <div style={{
          position: 'absolute', top: 14, right: 14,
          fontSize: 10, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase',
          padding: '4px 9px', borderRadius: 999,
          background: highlight ? '#EFF6FF' : '#F1F5F9', color: highlight ? '#1D4ED8' : '#475569',
        }}>
          {badge}
        </div>
      )}
      <div style={{
        width: 42, height: 42, borderRadius: 11,
        background: highlight ? '#EFF6FF' : '#F1F5F9',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
      <div style={{ fontSize: 17, fontWeight: 800, color: '#0F2742', letterSpacing: '-0.005em' }}>{title}</div>
      <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.55, flex: 1 }}>{body}</div>
      <div style={{
        marginTop: 'auto',
        display: 'flex', alignItems: 'center', gap: 8,
        color: disabled ? '#94A3B8' : (highlight ? '#1D4ED8' : '#0F2742'),
        fontSize: 13, fontWeight: 700,
      }}>
        {cta}
        {!disabled && <ArrowRight className="w-4 h-4" />}
      </div>
    </div>
  );
  return card;
}

export default function ScopeEntryGate({ onUploadPlan, onDimensions, onSCOPEVision, open }) {
  const { user } = useAuth();
  const [dismissedGate, setDismissedGate] = useState(false);

  // Only show the auth modal if the user isn't logged in AND the gate isn't
  // manually dismissed. Once they authenticate, we move straight to method
  // choice — no flicker.
  const showAuthGate = open && !user && !dismissedGate;
  const showMethodChooser = open && !!user;

  if (!open) return null;

  return (
    <div data-testid="scope-entry-gate" style={{ position: 'relative' }}>
      {/* The method chooser always sits underneath the modal. When the user
          is logged in, this is the primary surface. */}
      <div style={{ filter: showAuthGate ? 'blur(8px)' : 'none', pointerEvents: showAuthGate ? 'none' : 'auto', transition: 'filter .25s ease' }}>
        <div style={{ textAlign: 'center', marginBottom: 26 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#1D4ED8', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>
            Step 2 of 4 · Choose your input
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: '#0F2742', margin: '0 0 10px', letterSpacing: '-0.02em' }}>
            How would you like to measure your project?
          </h1>
          <p style={{ fontSize: 15, color: '#475569', margin: 0, lineHeight: 1.6, maxWidth: 640, margin: '0 auto' }}>
            Pick one — you can switch later. Every option feeds SCOPE and produces contractor-ready documents.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, maxWidth: 1040, margin: '0 auto' }}>
          <MethodCard
            testid="method-upload-plan"
            icon={<FileImage className="w-5 h-5" style={{ color: '#1D4ED8' }} />}
            badge="Most accurate"
            highlight
            title="I have a floor plan"
            body="Upload a plan, set the scale by clicking two known points, then trace each room. The area, perimeter and door counts flow straight into your Schedule of Works."
            cta="Open drawing tool"
            onClick={() => { if (showMethodChooser) onUploadPlan?.(); }}
            disabled={!showMethodChooser}
          />
          <MethodCard
            testid="method-dimensions"
            icon={<Ruler className="w-5 h-5" style={{ color: '#475569' }} />}
            badge="Fastest"
            title="I'll enter room dimensions"
            body="No plan needed. Type each room's length and width, pick finishes, and we'll build the scope from your numbers."
            cta="Continue with dimensions"
            onClick={() => { if (showMethodChooser) onDimensions?.(); }}
            disabled={!showMethodChooser}
          />
          <MethodCard
            testid="method-scopevision"
            icon={<Sparkles className="w-5 h-5" style={{ color: '#7C3AED' }} />}
            badge="NEW"
            title="SCOPEvision — measure from plan"
            body="Upload a floor plan and let SCOPEvision identify rooms and areas automatically. Ideal for estate agent and architect plans with printed labels — about 10 seconds."
            cta="Try SCOPEvision"
            onClick={() => { if (showMethodChooser) onSCOPEVision?.(); }}
            disabled={!showMethodChooser}
          />
        </div>
      </div>

      {showAuthGate && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 60,
            background: 'rgba(15, 23, 42, 0.38)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
          data-testid="scope-entry-auth-overlay"
        >
          <AuthCard onAuthed={() => setDismissedGate(true)} />
        </div>
      )}
    </div>
  );
}
